import { skipToken, useQuery } from '@tanstack/react-query'
import { useOrganization } from '@/contexts/OrganizationContext'
import { createTenantDb } from '@/lib/db-tenant'
import type { CreditCardTransactionsQuery } from '@/lib/credit-card-transactions-db'
import { BANKING_KEYS, keepPreviousForOrg } from '@/lib/query-keys'

/**
 * One page of a credit card's transactions in a date range, newest first, with
 * the count and total of the whole range — the "View transactions" dialog's list.
 *
 * Keeps the previous page on screen while the next one loads (same org only).
 *
 * @param query - the card, the range and the page; `null` while there is
 *   nothing valid to ask for (e.g. To before From), which keeps the query idle
 */
export function useCreditCardTransactions(query: CreditCardTransactionsQuery | null) {
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useQuery({
    queryKey: BANKING_KEYS.creditCardTransactions(orgId, query),
    queryFn:
      orgId && query
        ? // `signal` cancels a read the user has paged or filtered away from.
          async ({ signal }) => {
            const { data, error } = await createTenantDb(orgId).creditCardTransactions.list(query, signal)
            if (error) throw new Error(error.message)
            return data
          }
        : skipToken,
    placeholderData: keepPreviousForOrg(orgId, 2),
  })
}
